/**
 * Tiny store for the membership "testing plan / cadence" view.
 *
 * First pass: the cadence page reuses the existing add-ons step chrome (the real
 * PersistentDesktopSidebar + ExpandableOrderSummary). When the user picks
 * "Become a member", we flip `active` and go to the "addons" step — where
 * StepRenderer swaps the add-ons grid for the cadence component, and the sidebar
 * shows a (mocked) "Ahead membership" line priced at the selected plan.
 *
 * `plan` lives here (not as local state in the cadence component) so the
 * reused sidebar/order summary can read it and the total updates live as the
 * user switches plan. Kept separate from the big booking flow store so it
 * doesn't touch the step state machine.
 *
 * Persistence across refresh is handled by the server session, not here: the
 * booking flow is server-authoritative (loadSession), so `active`/`plan` ride in
 * the session snapshot (SessionStateSnapshot) and loadSession restores them.
 * A local persist layer here would just be clobbered by the product step's
 * mount-time reset during init, so it's intentionally omitted.
 */
import { create } from "zustand";
import type { RecommendedPlan } from "@/utils/membershipPricing";

// Cadence prices are resolved from the real membership tiers (Stripe source of
// truth) via `@/utils/membershipPricing` — not stored here.

interface MembershipPlanState {
  /** When true, the "addons" step renders the membership cadence instead of add-ons. */
  active: boolean;
  setActive: (active: boolean) => void;
  /** Selected cadence — drives the mocked membership price in the order summary. */
  plan: RecommendedPlan;
  setPlan: (plan: RecommendedPlan) => void;
  /** True when a membership is attached to an MRI scan (the bundle), so the
   *  product-step / tab-switch resets preserve it instead of dropping it. */
  bundleAttached: boolean;
  setBundleAttached: (v: boolean) => void;
  /** True when the user entered via the membership-first path (blood-only tab →
   *  "Become a member"). Used to suppress the add/remove membership upsell card
   *  on the add-ons step, which is only meaningful in the MRI-first path. */
  membershipFirst: boolean;
  setMembershipFirst: (v: boolean) => void;
}

export const useMembershipPlanStore = create<MembershipPlanState>((set) => ({
  active: false,
  setActive: (active) => set({ active }),
  plan: "baseline",
  setPlan: (plan) => set({ plan }),
  bundleAttached: false,
  setBundleAttached: (bundleAttached) => set({ bundleAttached }),
  membershipFirst: false,
  setMembershipFirst: (membershipFirst) => set({ membershipFirst }),
}));
